import { useRef, useState } from 'react'
import { FocusTrapDialog } from '../../components/FocusTrapDialog'
import { InlineAlert } from '../../components/InlineAlert'
import { ApiError } from '../../api/problem'
import { createBranch, patchBranch } from '../../api/branches'
import { LegalEntityLookupField } from './LegalEntityLookupField'
import type { BranchView } from '../../types/branch'

export interface BranchFormValues {
  name: string
  legalEntity: { id: string; name: string } | null
  country: string
  region: string
  isPermanentEstablishment: boolean
}

export interface BranchFormModalProps {
  mode: 'create' | 'edit'
  /** Required in edit mode: the branch being edited and its known version (If-Match). */
  editing?: { branch: BranchView; version: number }
  onClose: () => void
  onSaved: (branch: BranchView) => void
}

const REGION_OPTIONS = ['Americas', 'EMEA', 'APAC']

function initialValues(editing?: BranchFormModalProps['editing']): BranchFormValues {
  if (!editing) {
    return { name: '', legalEntity: null, country: '', region: '', isPermanentEstablishment: false }
  }
  const b = editing.branch
  return {
    name: b.name,
    legalEntity: { id: b.legalEntityId, name: b.legalEntityName ?? b.legalEntityId },
    country: b.country,
    region: b.region,
    isPermanentEstablishment: b.isPermanentEstablishment,
  }
}

/**
 * The single New-branch / Edit-branch form — real `POST`/`PATCH`, no
 * navigation on success (Branch has no detail page; the caller replaces the
 * row in place, mirroring `AccountFormModal`'s shape minus the routing).
 * `legalEntityId` is re-pointable on PATCH (design §5 decision 7).
 */
export function BranchFormModal({ mode, editing, onClose, onSaved }: BranchFormModalProps) {
  const [values, setValues] = useState<BranchFormValues>(() => initialValues(editing))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  function update<K extends keyof BranchFormValues>(key: K, value: BranchFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const isValid =
    values.name.trim() !== '' &&
    values.legalEntity !== null &&
    values.country.trim() !== '' &&
    values.region.trim() !== ''

  async function handleSubmit() {
    if (!isValid || submitting || !values.legalEntity) return
    setSubmitting(true)
    setError(null)
    try {
      if (mode === 'create') {
        const { branch } = await createBranch({
          legalEntityId: values.legalEntity.id,
          name: values.name.trim(),
          country: values.country.trim(),
          region: values.region.trim(),
          isPermanentEstablishment: values.isPermanentEstablishment,
        })
        onSaved(branch)
      } else if (editing) {
        const { branch } = await patchBranch(
          editing.branch.id,
          {
            legalEntityId: values.legalEntity.id,
            name: values.name.trim(),
            country: values.country.trim(),
            region: values.region.trim(),
            isPermanentEstablishment: values.isPermanentEstablishment,
          },
          editing.version,
        )
        onSaved(branch)
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail ?? err.message)
      } else {
        setError('Something went wrong saving this branch. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="branch-form-title"
      title={mode === 'create' ? 'New branch' : `Edit ${editing?.branch.name ?? 'branch'}`}
      onClose={onClose}
      initialFocusRef={nameRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="iw-btn iw-btn--primary"
            onClick={handleSubmit}
            disabled={!isValid || submitting}
          >
            {submitting ? 'Saving…' : mode === 'create' ? 'Create branch' : 'Save changes'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="branch-name">
            Name
          </label>
          <input
            id="branch-name"
            ref={nameRef}
            className="iw-input"
            value={values.name}
            required
            onChange={(e) => update('name', e.target.value)}
          />
        </div>

        <LegalEntityLookupField
          id="branch-legal-entity"
          label="Legal entity"
          value={values.legalEntity}
          onChange={(next) => update('legalEntity', next)}
        />

        <div style={{ display: 'flex', gap: 'var(--iw-space-3)' }}>
          <div className="iw-field" style={{ flex: 1 }}>
            <label className="iw-field-label" htmlFor="branch-country">
              Country
            </label>
            <input
              id="branch-country"
              className="iw-input"
              value={values.country}
              required
              onChange={(e) => update('country', e.target.value)}
            />
          </div>
          <div className="iw-field" style={{ flex: 1 }}>
            <label className="iw-field-label" htmlFor="branch-region">
              Region
            </label>
            <select
              id="branch-region"
              className="iw-select"
              value={values.region}
              onChange={(e) => update('region', e.target.value)}
              required
            >
              <option value="" disabled>
                Select…
              </option>
              {REGION_OPTIONS.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--iw-size-sm)' }}>
          <input
            type="checkbox"
            checked={values.isPermanentEstablishment}
            onChange={(e) => update('isPermanentEstablishment', e.target.checked)}
          />
          Permanent establishment
        </label>
      </form>
    </FocusTrapDialog>
  )
}
